import { Injectable } from '@angular/core';
import { fromEvent, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { PlaybackService } from './playback.service';

export interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  metaKey?: boolean;
  description: string;
  action: () => void | Promise<void>;
  global?: boolean; // Whether this shortcut works globally or only when focused
}

@Injectable({
  providedIn: 'root'
})
export class KeyboardService {
  private destroy$ = new Subject<void>();
  private shortcuts: KeyboardShortcut[] = [];
  private isEnabled: boolean = true;

  constructor(private playbackService: PlaybackService) {
    this.initializeGlobalShortcuts();
    this.setupKeyboardListener();
  }

  private initializeGlobalShortcuts(): void {
    this.shortcuts = [
      // Playback controls
      {
        key: ' ',
        description: 'Play/Pause',
        action: () => this.playbackService.togglePlay(),
        global: true
      },
      {
        key: 'ArrowRight',
        shiftKey: true,
        description: 'Next track',
        action: () => this.playbackService.nextTrack(),
        global: true
      },
      {
        key: 'ArrowLeft',
        shiftKey: true,
        description: 'Previous track',
        action: () => this.playbackService.previousTrack(),
        global: true
      },
      {
        key: 'ArrowUp',
        ctrlKey: true,
        description: 'Volume up',
        action: () => this.adjustVolume(10),
        global: true
      },
      {
        key: 'ArrowDown',
        ctrlKey: true,
        description: 'Volume down',
        action: () => this.adjustVolume(-10),
        global: true
      },
      {
        key: 'ArrowRight',
        ctrlKey: true,
        description: 'Seek forward 10s',
        action: () => this.seekRelative(10000),
        global: true
      },
      {
        key: 'ArrowLeft',
        ctrlKey: true,
        description: 'Seek backward 10s',
        action: () => this.seekRelative(-10000),
        global: true
      },
      {
        key: 's',
        ctrlKey: true,
        description: 'Toggle shuffle',
        action: () => this.playbackService.toggleShuffle(),
        global: true
      },
      {
        key: 'r',
        ctrlKey: true,
        description: 'Cycle repeat mode',
        action: () => this.playbackService.cycleRepeatMode(),
        global: true
      },
      {
        key: 'm',
        ctrlKey: true,
        description: 'Mute/Unmute',
        action: () => this.toggleMute(),
        global: true
      },
      
      // Seeking with number keys (0-9 for 0%-90% position)
      ...Array.from({ length: 10 }, (_, i) => ({
        key: i.toString(),
        description: `Seek to ${i * 10}%`,
        action: () => this.seekToPercentage(i * 10),
        global: true
      })),

      // Media keys (these may not work in all browsers)
      {
        key: 'MediaPlayPause',
        description: 'Play/Pause (Media key)',
        action: () => this.playbackService.togglePlay(),
        global: true
      },
      {
        key: 'MediaTrackNext',
        description: 'Next track (Media key)',
        action: () => this.playbackService.nextTrack(),
        global: true
      },
      {
        key: 'MediaTrackPrevious',
        description: 'Previous track (Media key)',
        action: () => this.playbackService.previousTrack(),
        global: true
      }
    ];
  }

  private setupKeyboardListener(): void {
    fromEvent<KeyboardEvent>(document, 'keydown')
      .pipe(
        filter(() => this.isEnabled),
        filter(event => this.shouldHandleEvent(event)),
        takeUntil(this.destroy$)
      )
      .subscribe(event => this.handleKeyboardEvent(event));
  }

  private shouldHandleEvent(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement;
    
    // Don't handle events when typing in input fields
    if (target && (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.contentEditable === 'true' ||
      target.isContentEditable
    )) {
      return false;
    }

    // Allow handling when focused on interactive elements with our keyboard handler
    if (target && target.hasAttribute('tabindex') && target.getAttribute('data-keyboard-enabled') === 'true') {
      return true;
    }

    return true;
  }

  private handleKeyboardEvent(event: KeyboardEvent): void {
    const shortcut = this.shortcuts.find(s => 
      s.key === event.code || s.key === event.key
    );

    if (!shortcut) return;

    // Check modifier keys
    const ctrlMatch = (shortcut.ctrlKey ?? false) === event.ctrlKey;
    const shiftMatch = (shortcut.shiftKey ?? false) === event.shiftKey;
    const altMatch = (shortcut.altKey ?? false) === event.altKey;
    const metaMatch = (shortcut.metaKey ?? false) === event.metaKey;

    if (ctrlMatch && shiftMatch && altMatch && metaMatch) {
      event.preventDefault();
      event.stopPropagation();
      
      try {
        shortcut.action();
      } catch (error) {
        console.error('Error executing keyboard shortcut:', error);
      }
    }
  }

  private async adjustVolume(delta: number): Promise<void> {
    try {
      const currentVolume = await this.playbackService.getVolume();
      const newVolume = Math.max(0, Math.min(1, currentVolume + (delta / 100)));
      await this.playbackService.setVolume(newVolume);
    } catch (error) {
      console.error('Failed to adjust volume:', error);
    }
  }

  private async seekRelative(deltaMs: number): Promise<void> {
    try {
      const currentState = await this.playbackService.getCurrentState();
      if (currentState) {
        const newPosition = Math.max(0, Math.min(currentState.duration, currentState.position + deltaMs));
        await this.playbackService.seek(newPosition);
      }
    } catch (error) {
      console.error('Failed to seek:', error);
    }
  }

  private async seekToPercentage(percentage: number): Promise<void> {
    try {
      const currentState = await this.playbackService.getCurrentState();
      if (currentState && currentState.duration > 0) {
        const newPosition = (percentage / 100) * currentState.duration;
        await this.playbackService.seek(newPosition);
      }
    } catch (error) {
      console.error('Failed to seek to percentage:', error);
    }
  }

  private lastVolume: number = 0.5;

  private async toggleMute(): Promise<void> {
    try {
      const currentVolume = await this.playbackService.getVolume();
      
      if (currentVolume > 0) {
        this.lastVolume = currentVolume;
        await this.playbackService.setVolume(0);
      } else {
        await this.playbackService.setVolume(this.lastVolume);
      }
    } catch (error) {
      console.error('Failed to toggle mute:', error);
    }
  }

  // Public methods for managing shortcuts
  public addShortcut(shortcut: KeyboardShortcut): void {
    this.shortcuts.push(shortcut);
  }

  public removeShortcut(key: string): void {
    this.shortcuts = this.shortcuts.filter(s => s.key !== key);
  }

  public getShortcuts(): KeyboardShortcut[] {
    return [...this.shortcuts];
  }

  public enable(): void {
    this.isEnabled = true;
  }

  public disable(): void {
    this.isEnabled = false;
  }

  public isKeyboardEnabled(): boolean {
    return this.isEnabled;
  }

  // Get shortcuts by category for help display
  public getPlaybackShortcuts(): KeyboardShortcut[] {
    return this.shortcuts.filter(s => 
      ['Play/Pause', 'Next track', 'Previous track', 'Toggle shuffle', 'Cycle repeat mode'].includes(s.description)
    );
  }

  public getVolumeShortcuts(): KeyboardShortcut[] {
    return this.shortcuts.filter(s => 
      s.description.includes('Volume') || s.description.includes('Mute')
    );
  }

  public getSeekingShortcuts(): KeyboardShortcut[] {
    return this.shortcuts.filter(s => 
      s.description.includes('Seek') || s.description.includes('%')
    );
  }

  public getMediaKeyShortcuts(): KeyboardShortcut[] {
    return this.shortcuts.filter(s => 
      s.description.includes('Media key')
    );
  }

  // Format shortcut display
  public formatShortcut(shortcut: KeyboardShortcut): string {
    const parts: string[] = [];
    
    if (shortcut.ctrlKey) parts.push('Ctrl');
    if (shortcut.shiftKey) parts.push('Shift');
    if (shortcut.altKey) parts.push('Alt');
    if (shortcut.metaKey) parts.push('Cmd');
    
    // Handle special keys
    let keyDisplay = shortcut.key;
    switch (shortcut.key) {
      case ' ':
        keyDisplay = 'Space';
        break;
      case 'ArrowUp':
        keyDisplay = '↑';
        break;
      case 'ArrowDown':
        keyDisplay = '↓';
        break;
      case 'ArrowLeft':
        keyDisplay = '←';
        break;
      case 'ArrowRight':
        keyDisplay = '→';
        break;
    }
    
    parts.push(keyDisplay);
    
    return parts.join(' + ');
  }

  public destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}